import type { CreditsHistoryItem, CreditsOperation } from '@/features/ai/creditsApi'
import { messagesNow } from '@/i18n'
import { knownJobTypeLabel } from '@/features/ai/lib/jobTypeLabel'
import { messages } from './messages'

export interface HistoryRowView {
  label: string
  /** Пустая строка — «вид операции не опознан, знак не обещаем». */
  sign: '+' | '−' | ''
  tone: 'positive' | 'negative' | 'neutral'
  /**
   * Есть ли что разворачивать. Считается по `detail`, а не по виду операции:
   * так решать велит контракт, и завтрашняя операция с деталями раскроется без
   * правки этого файла.
   */
  canExpand: boolean
}

/**
 * Четыре вида строк, а не два. «Пополнение» для бонуса неверно — автор за него
 * не платил; «списание» для возврата тем более. Подписи разные, а разворачивать
 * у трёх приходных видов нечего.
 *
 * `Map`, а не литерал: ключ `constructor` у объекта вернул бы функцию мимо
 * фолбэка (та же причина, что у `PHASE_BY_STATUS` и `KNOWN_MESSAGES`).
 */
type Messages = (typeof messages)['ru']

type OperationView = Omit<HistoryRowView, 'canExpand' | 'label'> & {
  label: (m: Messages) => string
}

const VIEW_BY_OPERATION = new Map<CreditsOperation, OperationView>([
  ['purchase', { label: (m) => m.opPurchase, sign: '+', tone: 'positive' }],
  ['bonus', { label: (m) => m.opBonus, sign: '+', tone: 'positive' }],
  ['refund', { label: (m) => m.opRefund, sign: '+', tone: 'positive' }],
  ['charge', { label: (m) => m.opCharge, sign: '−', tone: 'negative' }],
])

export const historyRowView = (item: CreditsHistoryItem): HistoryRowView => {
  const known = VIEW_BY_OPERATION.get(item.operation)
  const canExpand = item.detail !== null
  // Незнакомый вид не выдумываем: показываем серверное описание и не обещаем
  // знак. Угаданный минус над приходом соврал бы автору про его деньги.
  const m = messagesNow(messages)
  if (!known)
    return { label: item.description ?? m.opUnknown, sign: '', tone: 'neutral', canExpand }
  return {
    label: chargeLabel(item) ?? known.label(m),
    sign: known.sign,
    tone: known.tone,
    canExpand,
  }
}

/**
 * У списания подпись — САМО действие («Генерация картинок», «Перевод»), а не
 * безликое «Списание»: автору важно, за что ушли кредиты. Тип прогона
 * незнакомый или его нет — остаётся «Списание».
 */
const chargeLabel = (item: CreditsHistoryItem): string | null => {
  if (item.operation !== 'charge') return null
  const label = knownJobTypeLabel(item.detail?.jobType)
  return label ? label.charAt(0).toUpperCase() + label.slice(1) : null
}
